import { useFormatDateTime } from '@treneo/shared/contexts';
import { useAppTranslations } from '@treneo/shared/i18n';
import { Box, DataGridPaginationMode, DataGridWrapper, Typography } from '@treneo/shared/ui';

import type { OrganizationMembershipHistoryRow } from '../../../data/types';
import { createOrganizationMembershipHistoryColumns } from '../../utils/organization-membership-history-table.utils';

type Props = Readonly<{
  history: OrganizationMembershipHistoryRow[];
  isLoading?: boolean;
  showUserColumn: boolean;
  page?: number;
  pageSize?: number;
  rowCount?: number;
  onPaginationChange?: (page: number, pageSize: number) => void;
}>;

export const OrganizationMembershipHistoryTable = ({
  history,
  isLoading = false,
  showUserColumn,
  page,
  pageSize,
  rowCount,
  onPaginationChange
}: Props) => {
  const { t } = useAppTranslations();
  const { formatDateTime } = useFormatDateTime();
  const columns = createOrganizationMembershipHistoryColumns({
    t,
    formatDateTime: (date, fallback) => formatDateTime(date, fallback),
    showUserColumn
  });

  const useServerPagination =
    rowCount !== undefined &&
    onPaginationChange !== undefined &&
    page !== undefined &&
    pageSize !== undefined;

  if (isLoading) {
    return <Typography>{t('common.loading')}</Typography>;
  }

  if (history.length === 0 && !useServerPagination) {
    return <Typography color='text.secondary'>{t('admin.noHistory')}</Typography>;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <DataGridWrapper
        rows={history}
        columns={columns}
        loading={isLoading}
        getRowId={(row: OrganizationMembershipHistoryRow) => row.id}
        paginationMode={useServerPagination ? DataGridPaginationMode.Server : undefined}
        page={useServerPagination ? page : undefined}
        pageSize={useServerPagination ? pageSize : undefined}
        rowCount={useServerPagination ? rowCount : undefined}
        onPaginationChange={useServerPagination ? onPaginationChange : undefined}
      />
    </Box>
  );
};
